import { Button, Divider, Link, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { IssueActions, IssueContextProperty } from ".";
import { IssueFeed, ItemDescriptionField, SignalRGroup, SnackbarContent, useDrawer } from "..";
import { Links } from "../../constants";
import { useSession, useSignalR } from "../../hooks";
import { queryKeys, useEditIssue, useGetIssueDetail, useRemoveIssueUpvote, useUpvoteIssue } from "../../http";
import { ITeamSummary } from "../../models";
import { IssueProperties } from "./IssueProperties";
import { Proposals } from "./Proposals";

interface IProps {
  issueGuid: string;
}

const IssueDrawerContent = (props: IProps) => {
  const user = useSession();
  const { isLoading, data: issue } = useGetIssueDetail(props.issueGuid);
  const { mutate: editIssue } = useEditIssue();
  const { mutate: upvoteIssue } = useUpvoteIssue();
  const { mutate: removeIssueUpvote } = useRemoveIssueUpvote();
  const { enqueueSnackbar } = useSnackbar();
  const { closeDrawer } = useDrawer();
  const [focusedProposalGuid, setFocusedProposalGuid] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.Issue(props.issueGuid));

    return () => {
      signalR.leaveGroup(SignalRGroup.Issue(props.issueGuid));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.issueGuid]);

  useEffect(() => {
    const handleShouldRefetchIssue = (issueGuid: string) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(issueGuid));
    };

    signalR.onShouldRefetchIssue(handleShouldRefetchIssue);
    return () => signalR.offShouldRefetchIssue(handleShouldRefetchIssue);
  }, [queryClient, signalR]);

  useEffect(() => {
    return () => setFocusedProposalGuid(undefined);
  });

  async function handleDescriptionChanged(value: string) {
    if (issue === undefined) {
      return;
    }

    editIssue({
      issueGuid: issue.guid,
      teamSlug: issue.assignedTeam?.slug,
      description: value,
      createdByUserId: issue.createdByUser.userId,
    });
  }

  async function handleUpvoteClicked() {
    if (issue === undefined) {
      return;
    }

    if (issue.upvotes.findIndex((x) => x.userId === user.userId) > -1) {
      removeIssueUpvote({
        issueGuid: issue.guid,
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
        userId: user.userId,
      });
    } else {
      upvoteIssue({
        issueGuid: issue.guid,
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
        user: {
          userId: user.userId,
          displayName: user.displayName,
          enabled: true,
          profilePhotoSmallUrl: user.profilePhotoSmallUrl,
        },
      });
    }
  }

  function handleTeamChanged(team: ITeamSummary | null) {
    if (issue === undefined) {
      return;
    }

    enqueueSnackbar(
      <SnackbarContent
        title={
          team ? (
            <>
              Issue moved to{" "}
              <Link onClick={(e) => e.stopPropagation()} href={Links.TeamImplementation(team.slug)}>
                {team.name}
              </Link>
              .
            </>
          ) : (
            "Issue removed from team."
          )
        }
      />,
      {
        action: <Button href={Links.IssueDetail(issue.guid)}>Open Issue</Button>,
      }
    );

    closeDrawer();
  }

  return (
    <>
      <Stack spacing={0.5} divider={<Divider variant="dashed" />} sx={{ px: { xs: 0.5, sm: 1 }, py: 0.5 }}>
        <Stack>
          {!isLoading && issue && <IssueContextProperty issue={issue} isEditable={issue.userCanEditOtherProperties} />}

          <ItemDescriptionField
            isLoading={isLoading}
            isEditable={issue?.userCanEditTitle ?? false}
            value={issue?.description}
            onChange={handleDescriptionChanged}
            variant="h5"
          />
        </Stack>

        <IssueActions
          isLoading={isLoading}
          issue={issue}
          onProposalCreated={(proposalGuid) => setFocusedProposalGuid(proposalGuid)}
          onUpvoteClicked={handleUpvoteClicked}
        />
        {!isLoading && issue && (
          <Proposals isLoading={isLoading} issue={issue} focusedProposalGuid={focusedProposalGuid} />
        )}
      </Stack>
      <Divider />
      <Stack divider={<Divider flexItem />} sx={{ overflow: "auto" }}>
        <IssueProperties issue={issue} isLoading={isLoading} onTeamChanged={handleTeamChanged} />
        <IssueFeed
          issueGuid={props.issueGuid}
          issueCreatedByUserGuid={issue?.createdByUser.userId}
          teamSlug={issue?.assignedTeam?.slug}
        />
      </Stack>
    </>
  );
};

export { IssueDrawerContent };
